import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatOverlayService } from 'src/app/services/mat-overlay.service';
import { OverlayConfig } from '@angular/cdk/overlay';
import { TableFilterService } from './table-filter.service';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { TableFilterDialogComponent } from './table-filter-dialog/table-filter-dialog.component';
import { HelperService } from 'src/app/utils/helper.service';

@Component({
  selector: 'app-table-filter',
  templateUrl: './table-filter.component.html',
  styleUrls: ['./table-filter.component.css']
})
export class TableFilterComponent implements OnDestroy, OnChanges {

  @Input() filters: any[] = [];
  @Input() defaultFilterLoading: boolean = false;
  @Input() defaultFilterPlaceholder: string = 'Informe o texto para pesquisa';
  @Output() filterChange: EventEmitter<any> = new EventEmitter<any>();
  defaultFilterForm: FormControl = new FormControl("");
  filterForm: FormGroup = new FormGroup({});
  chipsSubscriber: Subscription;
  defaultFilterSubscriber: Subscription;
  chips: any[] = [];

  constructor(
    private helper: HelperService,
    private dialog: MatDialog,
    private tableFilterService: TableFilterService,
    private fb: FormBuilder
  ) {
    this.tableFilterService.getFilter().subscribe(filter => {
      this.ObjectKeys(filter).forEach(key => {
        this.filterForm.get(key)?.patchValue(filter[key]);
      });
    });

    this.defaultFilterSubscriber = this.defaultFilterForm.valueChanges
      .pipe(
        debounceTime(500),
        distinctUntilChanged()
      )
      .subscribe(() => {
        this.filterChange.emit(this.filtersOutput());
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.filters && changes.filters.currentValue) {
      const filters = changes.filters.currentValue;
      const formGroup = filters.map(filter => {
        switch (filter.type) {
          case 'select-multiple':
            return {
              [filter.name]: new FormControl(filter.defaultFilter)
            }
          case 'select':
            return {
              [filter.name]: new FormControl(filter.defaultFilter)
            }
          case 'date-range':
            return {
              [filter.name]: new FormGroup({
                inicio: new FormControl(filter.defaultFilter.inicio),
                fim: new FormControl(filter.defaultFilter.fim)
              })
            }
          case 'date':
            return {
              [filter.name]: new FormControl(filter.defaultFilter)
            }
          default:
            return {
              [filter.name]: new FormControl(filter.defaultFilter)
            }
        }
      }).reduce((acc, cur) => {
        return { ...acc, ...cur }
      }, {});

      this.filterForm = this.fb.group(
        formGroup
      );
      this.filterForm.valueChanges.subscribe(() => {
        this.filterChange.emit(this.filtersOutput());
      });
    }
  }

  ngOnDestroy() {
    if (this.defaultFilterSubscriber) {
      this.defaultFilterSubscriber.unsubscribe();
    }
  }

  onSearchTrigger() {
    this.filterChange.emit(this.filtersOutput());
  }

  openDialog(event: MouseEvent): void {
    const origin = event.target as HTMLElement;
    const dialogRef = this.dialog.open(TableFilterDialogComponent, {
      position: { left: `${origin.getBoundingClientRect().x - 450}px`, top: `${origin.getBoundingClientRect().y + 52}px` },
      width: '500px',
      maxHeight: '90vh',
      maxWidth: '100%',
      disableClose: false,
      backdropClass: 'table-filter-backdrop',
      data: { filters: this.filters, filterForm: this.filterForm }
    });
    const updatePosition = () => {
      dialogRef.updatePosition({ left: `${origin.getBoundingClientRect().x - 450}px`, top: `${origin.getBoundingClientRect().y + 52}px` })
    }
    window.addEventListener('resize', updatePosition);

    dialogRef.afterClosed().subscribe(result => {
      window.removeEventListener('resize', updatePosition);
    });
  }

  filtersOutput(): any {
    const filterFormValue = this.filterForm.value;

    const defaultFilterValue = this.defaultFilterForm.value;

    let defaultFilter = {};
    if (defaultFilterValue && defaultFilterValue.trim() !== '') {
      const searchText = defaultFilterValue.trim();

      const isCPF = /^\d{11}$/.test(searchText);
      const isNumeric = /^\d+$/.test(searchText);
      if (isCPF) {
        defaultFilter = { 'filter[cpf_agente_instrucao]': searchText };
      } else if (isNumeric) {
        defaultFilter = { 'filter[numero_processo_sei]': searchText };
      } else {
        const orgaoFilter = this.filters.find(f => f.name === 'orgao_id');
        if (orgaoFilter && orgaoFilter.options && orgaoFilter.options.length > 0) {
          const orgaoEncontrado = orgaoFilter.options.find(opt =>
            opt.label.toLowerCase().includes(searchText.toLowerCase())
          );
          if (orgaoEncontrado) {
            defaultFilter = { 'filter[orgao_id]': orgaoEncontrado.value };
          } else {
            defaultFilter = { 'filter[numero_processo_sei]': searchText };
          }
        } else {
          defaultFilter = { 'filter[numero_processo_sei]': searchText };
        }
      }
    }

    const filterOut = this.ObjectKeys(filterFormValue).map(key => {
      switch (this.filters.find(filter => filter.name === key).type) {
        case 'select-multiple':
          if (!this.compareArrays(filterFormValue[key], this.filters.find(filter => filter.name === key).defaultFilter)) {
            const keyString = `filter[${key}]`;
            const value = Array.isArray(filterFormValue[key]) ? filterFormValue[key] : [filterFormValue[key]];
            if (value.length > 0 && value[0] !== null && value[0] !== undefined) {
              return { [keyString]: value };
            }
          }
          break;
        case 'select':
          if (filterFormValue[key] !== null && filterFormValue[key] !== undefined && filterFormValue[key] !== '') {
            const keyString = `filter[${key}]`;
            return { [keyString]: filterFormValue[key] };
          }
          break;
        case 'date-range':
          if (filterFormValue[key].inicio && filterFormValue[key].fim) {
            const keyStringInicio = `filter[${key}Desde]`;
            const keyStringFim = `filter[${key}Ate]`;
            return { [keyStringInicio]: this.formatFilterData(filterFormValue[key].inicio), [keyStringFim]: this.formatFilterData(filterFormValue[key].fim) };
          }
          if (filterFormValue[key].inicio) {
            const keyStringInicio = `filter[${key}Desde]`;
            return { [keyStringInicio]: this.formatFilterData(filterFormValue[key].inicio) };
          }
          if (filterFormValue[key].fim) {
            const keyStringFim = `filter[${key}Ate]`;
            return { [keyStringFim]: this.formatFilterData(filterFormValue[key].fim) };
          }
          break;
        case 'date':
          if (filterFormValue[key]) {
            const keyString = `filter[${key}]`;
            return { [keyString]: this.formatFilterData(filterFormValue[key]) };
          }
          break;
        default:
          const keyString = `filter[${key}]`;
          return { [keyString]: filterFormValue[key] };
      }
    }).filter(filter => filter !== undefined).reduce((acc, cur) => {
      return { ...acc, ...cur }
    }, {});

    return { ...defaultFilter, ...filterOut };
  }


  updateChips() {
    const filter = this.filterForm.value;

  }

  clearFilters(){
    this.filters.forEach(filter => {
      this.filterForm.get(filter.name)?.setValue(filter.defaultFilter);
    });
  }

  removeChip(formControl, defaultFilter) {
    formControl.setValue(defaultFilter);
  }
  removeSelectMultipleChip(formControl, chip) {
    formControl.setValue(formControl.value.filter(c => c !== chip));
  }

  getChipColor(chip, chipOptions) {
    const chipColor = chipOptions.find(option => option.value === chip).color;
    return chipColor ? chipColor : '#e0e0e0';
  }
  getChipLabel(chip, chipOptions) {
    return chipOptions.find(option => option.value === chip).label;
  }
  formatChipData(data) {
    return new Date(data).toLocaleDateString()
  }
  formatFilterData(data): string {
    return this.helper.formatarData(data).split(' ')[0];
  }
  compareArrays(array1, array2): boolean {
    if (array1.length !== array2.length) {
      return false;
    }
    for (let i = 0; i < array2.length; i++) {
      if (array1[i] !== array2[i]) {
        return false;
      }
    }
    return true;
  }
  ObjectKeys(obj): string[] {
    return Object.keys(obj);
  }
  ObjectValues(obj): any[] {
    return Object.values(obj);
  }
}
